import { defineEffect } from '../../../core/effect';
import { tween } from '../../../core/effect/motion';
import { parseZoneKey } from '../../../core/field/zones';
import {
  CARD_ASPECT, add, faceArt, fade, playerColor,
} from '../fx/common';
import { imageReady } from '../fx/images';
import {
  numText, objStyle, paintCard, rgbaCss, statFillRGBA,
} from '../drawObjects';
import { liftOf, turnOf } from '../fx/flipOpen';

/**
 * **ステータスの変化**（Issue #584。指示書 `../direction/stat_change.md`）。
 *
 * core はステータスの変化を MSG に出さないので、port（`editorPorts/statChanges.js`）が `board_steps` の `stat_changes` から
 * 疑似イベント `stat_change` を作る（payload の `cards[]` に `{ uid, zone, player, from, to, base }`）。**原因は読まない**
 * （倍・半分・元に戻る・永続効果の掛け直しは値からは読み分けられない）。観測できる結果（前後の値・元々の値）だけで見せる。
 *
 * ```
 * 0〜600   盤のそのカードの ATK / DEF の帯の数字が from → to へ数え送られる（ease-out。最後はゆっくり着地）。
 *          数字の色も同時に、元々の値との比較の色（上 = 赤・下 = 青・同じ = 今の色）へ移る
 * 600〜700 着地で数字が一瞬ふくらんで戻る（1.2 → 1.0）。ほかの飾りは付けない（盤の小さな変化なので控えめ）
 * ```
 *
 * 盤面は帯の終わり（700ms）まで前の値のまま（`board: { at: D }`）。帯の間は、盤のカードの上面と同じ描き方
 * （`../drawObjects.js::paintCard`）で**数え送っている値のカードの上面**を盤のカードにぴったり重ねる
 * （上面の絵を 1 フレームごとに描き直す。`tweaks` は three の置き方しか触れず、上面の絵の中身は変えられないため）。
 * 帯の終わりで盤面が進み、同じ値・同じ色の盤のカードに引き継ぐ。何体もまとめて変わったら同じ時刻に一斉に数える。
 */
export const D = 700;
export const COUNT = 600;
const POP_UP = 625;

/** 数え送りの進み（0 → 1。ease-out で最後はゆっくり） */
export const countAt = (t) => tween(t, 0, COUNT, 'power4_out');
/** 着地のふくらみ（600 で 1 → 625 で 1.2 → 700 で 1） */
export const popAt = (t) => {
  if (t < COUNT || t >= D) return 1;
  if (t < POP_UP) return 1 + 0.2 * tween(t, COUNT, POP_UP, 'power2_out');
  return 1.2 - 0.2 * tween(t, POP_UP, D, 'ease_out');
};

const lerp = (a, b, k) => a + (b - a) * k;
/** 数え送っている値（整数） */
export const countValue = (from, to, k) => (Number.isFinite(from) && Number.isFinite(to) ? Math.round(lerp(from, to, k)) : to);

/**
 * そのフレームの帯の数字（値・色・ふくらみ）。**時刻の純関数**
 *
 * @param c payload の 1 枚（`{ from, to, base }`）
 * @param defense 守備表示か
 */
export function statsAt(c, defense, t) {
  const k = countAt(t);
  const pop = popAt(t);
  const one = (which) => {
    const from = c?.from?.[which];
    const to = c?.to?.[which];
    const base = c?.base?.[which];
    const value = countValue(from, to, k);
    const a = statFillRGBA(which, Number.isFinite(from) ? from : to, base, defense);
    const b = statFillRGBA(which, to, base, defense);
    const changed = Number.isFinite(from) && Number.isFinite(to) && from !== to;
    return { value, fill: rgbaCss(a.map((x, i) => lerp(x, b[i], k))), scale: changed ? pop : 1 };
  };
  const atk = one('atk');
  const def = one('def');
  return {
    atk: atk.value, def: def.value, defense,
    atkText: numText(atk.value), defText: numText(def.value),
    atkFill: atk.fill, defFill: def.fill, atkScale: atk.scale, defScale: def.scale,
  };
}

const isMonsterZone = (zone) => parseZoneKey(zone)?.kind === 'monster';

export default defineEffect({
  situation: 'stat_change',
  role: 'motion',
  label: 'ステータスの変化',
  color: '#ff3b30',
  /*
   * 盤に表側で居るモンスターだけ（盤面は帯の頭でこのイベントの後。値は変わっていても置き場所と表示形式は同じ）。
   * **出す条件は絞らない**（原因を問わず、場に居続けたモンスターの値が変わったら出す）
   */
  // `cards` は分岐レイヤー（`editorPorts/duelSituations/stat_change.js`）が場の表側のモンスターに絞り、zone / code / defense を付けて渡す（Issue #589）
  duration: () => D,
  // 盤面（帯の数字）は数え終わってから新しい値へ
  board: () => ({ at: D }),

  layers: [
    {
      id: 'count',
      label: '数え送る ATK / DEF の帯（盤のカードの上面に重ねる）',
      space: 'board',
      script: (ctx) => {
        const { THREE, kit, payload } = ctx;
        const root = new THREE.Group();
        const f = ctx.stage?.field;
        const unit = f?.unit > 0 ? f.unit : 0;
        const plane = f?.plane ?? null;
        if (!(unit > 0) || !plane) return { root };
        const v = objStyle(ctx.style);
        const cw = unit;
        const ch = unit / CARD_ASPECT;
        const res = Math.max(1, 360 / cw);
        const faces = (payload?.cards ?? []).map((c, i) => {
          const at = ctx.anchor?.({ card: c.uid, else: { zone: c.zone ?? '' } });
          if (!at?.plane) return null;
          const pos = { faceUp: true, defense: Boolean(c.defense) };
          const yaw = (Number(f.cardAngle?.(at.zone ?? c.zone)) || 0) + turnOf(pos);
          const y = liftOf(pos, ctx.style) + v.cardThickness + 0.02;
          const cv = typeof document !== 'undefined' ? document.createElement('canvas') : null;
          if (cv) { cv.width = Math.round(cw * res); cv.height = Math.round(ch * res); }
          const tex = cv ? kit.textureFromCanvas(cv) : null;
          const mesh = kit.onBoard(kit.quad(kit.plain(tex, 1), cw, ch));
          const g = new THREE.Group();
          add(root, g, 22 + i * 0.01);
          add(g, mesh, 22 + i * 0.01);
          g.position.set(at.plane[0] - plane.w / 2, y, at.plane[1] - plane.h / 2);
          g.rotation.y = yaw;
          const art = faceArt(ctx, { code: c.code, player: c.player });
          const name = c.code != null ? (ctx.cards?.name?.(c.code) ?? String(c.code)) : '';
          const color = playerColor(ctx, c.player);
          let key = null;
          const paint = (t) => {
            const g2 = cv?.getContext?.('2d');
            if (!g2) return;
            const stats = statsAt(c, Boolean(c.defense), t);
            const k = JSON.stringify([stats.atkText, stats.defText, stats.atkFill, stats.defFill, stats.atkScale, stats.defScale, imageReady(art)]);
            if (k === key) return;
            key = k;
            paintCard(g2, { size: { w: cv.width, h: cv.height } }, {
              card: { faceUp: true, code: c.code }, art: imageReady(art) ? art : null, back: null, name, color, stats, v,
            });
            if (tex) tex.needsUpdate = true;
          };
          return { g, paint };
        }).filter(Boolean);
        return {
          root,
          update: ({ localMs: t }) => {
            faces.forEach(({ g, paint }) => {
              paint(t);
              // 帯の終わりには盤のカード（新しい値）に引き継いで何も残さない
              fade(g, t >= 0 && t < D ? 1 : 0);
            });
          },
        };
      },
    },
  ],
}, 'stat_change_motion.js');
